export type PopupRows = Array<[label: string, value: unknown]>;

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function formatPropertyLabel(key: string): string {
  const spaced = key.replaceAll("_", " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatPropertyValue(value: unknown): string {
  if (value !== null && typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Builds popup rows from GeoJSON feature properties.
 * Internal properties (prefixed with `_`) and empty values are skipped,
 * label and value are HTML escaped because the popup is rendered as HTML.
 */
export function buildPopupRowsFromProperties(properties: Record<string, unknown> | null | undefined): PopupRows {
  return Object.entries(properties ?? {})
    .filter(([key, value]) => !key.startsWith("_") && value != null && String(value) !== "")
    .map(([key, value]): [string, string] => [
      escapeHtml(formatPropertyLabel(key)),
      escapeHtml(formatPropertyValue(value)),
    ]);
}

export function renderRows(rows: PopupRows): string {
  return rows
    .filter(([, v]) => v != null && String(v) !== "")
    .map(
      ([k, v]) =>
        `<tr class="nwa-popover__row"><th class="nwa-popover__th">${k}</th><td class="nwa-popover__td">${String(
          v,
        )}</td></tr>`,
    )
    .join("");
}

export function renderPopup(options: {
  title: string;
  severity?: string | null; // optional für NWA
  rows: PopupRows;
  theme?: "nwa" | "thunderstorm";
}): string {
  const theme = options.theme ?? "nwa";
  const severityAttr = options.severity ? ` data-severity="${options.severity}"` : "";
  const severityDot = options.severity ? `<span class="nwa-popover__severity-dot"${severityAttr}></span>` : "";

  return `
    <div class="nwa-popover ${theme === "thunderstorm" ? "nwa-popover--thunderstorm" : ""}">
      <div class="nwa-popover__header">
        <span class="nwa-popover__title">${options.title}</span>
        ${severityDot}
      </div>
      <table class="nwa-popover__table">
        ${renderRows(options.rows)}
      </table>
    </div>`;
}
